import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { Paper, Typography, Tooltip, IconButton, Fab, withStyles } from '@material-ui/core';
import { Refresh, Add, Delete } from '@material-ui/icons';
import MaterialTable from 'material-table';
import { MuiTableRows } from '../../../themes';
import { BackButton, hasPermission, localization, hasAtLeastOnePermission, getDimensionsOfPagination } from '../../sc-utilities';
import Toolbar from '../../sc-utilities/Toolbar'
import * as actions from './store/gradeActions';
import * as action from '../../np-core/Auth/store/authActions';
import GradeDelete from './GradeDelete';
import Export from '../../sc-components/ExportForm/';
import * as Yup from "yup";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import {bulkVisibilityEditGrades} from "./store/gradeActions";

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
    },
    customToolbar: {
        marginTop: 9,
        marginLeft: 14,
        width: "900px"
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

let initialValues = [
    'Condition',
    'InternationalGrading',
    'System',
    'Visible',
];
const route = 'grades';

const GradeList = props => {
    const { classes, translations, deleting, onGrade, onGradeCount, saveColumnsFilter, activeCustomerID, tableColumns, onBulkDeleteGrades, permissions, redirectHandler, tableRef, onBulkVisibilityEditGrades } = props;

    const columnObject = useMemo(() => tableColumns, [tableColumns]);
    const [deleteModalState, setDeleteModalState] = useState({ open: false, GradeIDs: [] });
    const selectionPermissions = hasAtLeastOnePermission(permissions, ['grade_delete', 'grade_visibility_update']);
    const windowHeight = hasPermission(permissions, 'grade_export') ? 330 : 290;

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: selectionPermissions,
        selectionProps: rowData => {
            let disabled = false;
            rowData.tableData.disabled = disabled
            return disabled ? {
                disabled: true,
                checked: false
            } : {}
        },
        search: false,
        minBodyHeight: window.innerHeight - windowHeight < 300 ? 300 : window.innerHeight - windowHeight,
        maxBodyHeight: window.innerHeight - windowHeight,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;
        let count = await onGradeCount(query);
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count ? await onGrade(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }

    const tableTitle = () => {
        return (

            <div className={classes.customToolbar}>
                <div className={classes.tableTitle}>
                    {BackButton(translations, classes.customTooltip)}
                    <Typography className={classes.typography}>{translations["grades"] || 'Grades'}</Typography>
                    {hasPermission(permissions, 'grade_create') ? <Fab
                        variant="extended"
                        size="small"
                        color="primary"
                        aria-label="Add"
                        className={classes.fab}
                        style={{ borderRadius: '1px' }}
                        onClick={() => redirectHandler(`/settings/grade/create`)}
                    >
                        <Add size="small" />
                        {translations["create_new"] || "Create new"}
                    </Fab> : null}
                    <Tooltip title={translations["refresh"] || "Refresh"}>
                        <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </div>
                {
                    hasPermission(permissions, 'grade_export') ?
                        <Export
                            route={route}
                            tableRef={tableRef}
                            initialValues={initialValues}
                            formSettings={formSettings}
                            showVisibilities={true}
                            showTypes={true}
                        /> : null
                }
            </div>
        )
    }

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`Grade`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    }

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, gradeIDs: [] });
    }

    const handleCloseDanger = async () => {
        await onBulkDeleteGrades({ GradeIDs: deleteModalState.gradeIDs, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, gradeIDs: [] });
        tableRef.current && tableRef.current.onQueryChange()
    };

    const handleBulkDelete = gradeIDs => {
        setDeleteModalState({ ...deleteModalState, open: true, gradeIDs: gradeIDs });
    };

    const handleBulkVisibility = async (ids, visibility) => {
        await onBulkVisibilityEditGrades({ ActiveCustomerID: activeCustomerID, GradeIDs: ids, Visible: visibility });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["grades"] || 'Grades'}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(grade => grade.GradeID))
        },
        {
            tooltip: `${translations["typical_problem_visibility_on"] || "Visibility on to all selected"} ${translations["grades"] || 'Grades'}`,
            icon: <VisibilityIcon />,
            onClick: (rowData) => handleBulkVisibility(rowData.map(grade => grade.GradeID), true)
        },
        {
            tooltip: `${translations["typical_problem_visibility_off"] || "Visibility off to all selected"} ${translations["grades"] || 'Grades'}`,
            icon: <VisibilityOffIcon />,
            onClick: (rowData) => handleBulkVisibility(rowData.map(grade => grade.GradeID), false)
        }
    ];

    let formSettings = {
        Validation: initialValues.reduce((o, k) => ({ ...o, [k]: Yup.boolean() }), {}),
        ButtonSubmit: hasPermission(permissions, "grade_export") ? {
            Title: translations["download"] || "Download",
            Color: 'primary'
        } : null
    };

    return (
        <Paper>
            <MaterialTable
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {hasPermission(permissions, 'grade_delete') ? (
                <GradeDelete
                    entityName={translations["grades"] || 'Grades'}
                    translations={translations}
                    deleting={deleting}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    deleteModalState={deleteModalState}
                />
            ) : null}
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID,
        translations: state?.translate?.translations?.activeTranslations || {},
        deleting: state?.setting?.grades?.deleting,
        permissions: state?.core?.login?.permissions || []
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onGradeCount: filter => dispatch(actions.fetchGradeCount(filter)),
        onGrade: filter => dispatch(actions.fetchGrade(filter)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        onBulkDeleteGrades: data => dispatch(actions.bulkDeleteGrades(data)),
        onBulkVisibilityEditGrades: data => dispatch(bulkVisibilityEditGrades(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(GradeList));


